import React, { useMemo, useState } from 'react';
import {
  DependencyForm,
  SearchBar
} from '../../../../../../pages/DashboardNew/components';
import DependencyCard from '../../../../../../shared/molecules/DependencyCard/DependencyCard';
import { DependencyCardData } from '../../../../../../shared/molecules/DependencyCard/DependencyCard.def';

const DependencyCardSkeleton = () => (
  <div className="mb-4 rounded-xl border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark shadow-sm p-0 overflow-hidden transition-all duration-300 animate-pulse">
    <div className="flex items-center px-4 pt-3 pb-1">
      <span className="w-5 text-base mr-3 flex-shrink-0 text-center">
        <div className="w-2 h-2 bg-light-surfaceSecondary dark:bg-gray-600 rounded-full mx-auto" />
      </span>
      <div className="flex-1 truncate">
        <div className="h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded w-2/3" />
      </div>
      <div className="ml-2 w-[18px] h-[18px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
    </div>
    <div className="flex items-center gap-2 px-4 pb-2">
      <div className="w-4 h-4 bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
      <div className="px-2 py-1 bg-light-surfaceSecondary dark:bg-gray-700 rounded-full">
        <div className="h-3 bg-light-surfaceActive dark:bg-gray-700 rounded w-16" />
      </div>
      <div className="ml-auto flex items-center gap-2">
        <div className="p-1.5 rounded-md">
          <div className="w-[14px] h-[14px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
        </div>
        <div className="p-1.5 rounded-md">
          <div className="w-[14px] h-[14px] bg-light-surfaceSecondary dark:bg-gray-600 rounded" />
        </div>
      </div>
    </div>
  </div>
);

const SkeletonList = () => {
    // Hardcoded for dependency type as we only use this for dependencies here
    const skeletonCount = 4;
    return (
        <div className="px-3 mt-1 py-3">
            <div>
            {Array.from({ length: skeletonCount }, (_, index) => (
                <DependencyCardSkeleton key={index} />
            ))}
            </div>
        </div>
    );
};

interface EpicDependencyTabProps {
  dependencyManagement: any;
  projects?: any[];
  readOnly?: boolean;
  simulation?: boolean;
  childEntityType?: string;
  singleRoadmapLoading?: boolean;
  onEntityClick: (id: string) => void;
}

const EpicDependencyTab = ({
  dependencyManagement,
  projects = [],
  readOnly = false,
  simulation = false,
  childEntityType = "Work Items",
  singleRoadmapLoading = false,
  onEntityClick
}: EpicDependencyTabProps) => {
  const [depSearch, setDepSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);



  // Adapter for DependencyForm to work with DependencyCard's EditorComponent prop
  const EditorAdapter = useMemo(() => ({ onCancel }: { onCancel: () => void }) => {
    const data = dependencyManagement.getProjectList();
    
    return (
        <DependencyForm
          isEditing={true}
          isLoading={dependencyManagement.isActionLoading}
          selectedProject={dependencyManagement.editDepProject}
          dependsOn={dependencyManagement.editDepDependsOn}
          projectOptions={data}
          dependencyOptions={dependencyManagement.getFilterProjectList(
            expandedId,
          )}
          onProjectChange={dependencyManagement.setEditDepProject}
          onDependsOnChange={dependencyManagement.setEditDepDependsOn}
          onSave={async () => {
            await dependencyManagement.handleUpdateDependency();
            onCancel();
            // Reset form state handled inside management or needs manual reset?
            // Based on previous code:
            dependencyManagement.setEditDep(null);
          }}
          onCancel={() => {
            dependencyManagement.setEditDep(null);
            onCancel();
          }}
          childEntityType={childEntityType}
        />
    );
  }, [dependencyManagement, expandedId, childEntityType]);


  return (
    <>
      <div className="depencyTab-container">
        <div className="depencyTab-header">
          <div className="depencyTab-search-wrapper">
            <SearchBar
              searchValue={depSearch}
              onSearchChange={setDepSearch}
              placeholder={`Search for dependencies`}
              statusFilters={[]}
              setStatusFilters={() => {}}
              statusDropdownOpen={false}
              setStatusDropdownOpen={() => {}}
              showStatusFilter={false}
            />
          </div>

          {!readOnly && (
            <button
              className="h-[40px]  px-3 text-xs font-medium bg-light-surfaceSecondary dark:bg-slate-700 dark:hover:bg-slate-600 text-light-text-primary dark:text-white rounded-xl  transition-colors duration-200 whitespace-nowrap hover:bg-light-interactive-hover"
              onClick={() => {
                dependencyManagement.setIsAddingDep(true);
                dependencyManagement.setAddDepProject("");
                dependencyManagement.setAddDepDependsOn([]);
                setExpandedId(null);
                dependencyManagement.setEditDep(null);
              }}
            >
              + Add
            </button>
          )}
        </div>

        <div className="flex-1 overflow-scroll">
          {singleRoadmapLoading ? (
            <SkeletonList />
          ) : (
            <>
              <div className="px-3 py-1">
                {/* Show project dependencies */}
                {dependencyManagement.dependenciesState.length > 0 &&
                  !dependencyManagement.isAddingDep &&
                  dependencyManagement.dependenciesState
                    .filter(
                      (dep: any) =>
                        (dep.project_name || "")
                          .toLowerCase()
                          .includes(depSearch.toLowerCase()) ||
                        dep.dependencies.some((d: any) =>
                          (d.project_name || d.label || "")
                            .toLowerCase()
                            .includes(depSearch.toLowerCase()),
                        ),
                    )
                    .map((dep: any, idx: number) => {
                      const isExpanded = expandedId === dep.project_id;


                      // Map to DependencyCardData
                      const cardData: DependencyCardData = {
                        id: dep.project_id,
                        type: "project", // Assuming project type
                        name: dep.project_name,
                        dependency: dep.dependencies.map((d: any) => ({
                          id: d.project_id || d.to || d.project_to,
                          type: d.type || "project",
                          name: d.project_name || d.label || "Unknown",
                          entity_type: dep.entity_type, // Adjust if needed
                          invalid_dependency: d.invalid_dependency,
                        })),
                        entity_type: dep.entity_type,
                      };


                      return (
                        <DependencyCard
                          key={dep.project_id}
                          dependency={cardData}
                          onRemove={() => {
                            if (!readOnly) {
                              // Use direct delete because Shared Card has its own confirmation
                              dependencyManagement.handleDeleteAllDependencies(
                                dep.project_id,
                              );
                            }
                          }}
                          onExpanded={(open) =>{

                            if (!open) {
                              onEntityClick(`project-${dep.project_id}`);
                            } else {
                              onEntityClick("");
                            }

                            setExpandedId(!open ? dep.project_id : null)
                          }
                          }
                          expanded={isExpanded}
                          onEdit={() => {
                            if (readOnly) return;

                            setExpandedId(dep.project_id);
                            dependencyManagement.setEditDep({
                              depId: dep.project_id,
                              linkIdx: idx,
                            });
                            dependencyManagement.setEditDepProject(
                              dep.project_id,
                            );
                            dependencyManagement.setEditDepDependsOn(
                              dep.dependencies.map(
                                (d: any) =>
                                  d.project_id || d.to || d.project_to,
                              ),
                            );
                            dependencyManagement.setIsAddingDep(false);
                          }}
                          entityType="Project"
                          onTitleClick={() =>{
                          }}
                          EditorComponent={EditorAdapter}
                        />
                      );
                    })}
              </div>

              {dependencyManagement.isAddingDep && !readOnly && (
                <DependencyForm
                  isEditing={false}
                  isLoading={dependencyManagement.isActionLoading}
                  selectedProject={dependencyManagement.addDepProject}
                  dependsOn={dependencyManagement.addDepDependsOn}
                  projectOptions={dependencyManagement.getProjectList()}
                  dependencyOptions={dependencyManagement.getFilterProjectList(
                    dependencyManagement.addDepProject,
                  )}
                  onProjectChange={dependencyManagement.setAddDepProject}
                  onDependsOnChange={dependencyManagement.setAddDepDependsOn}
                  onSave={dependencyManagement.handleAddDependency}
                  onCancel={() => dependencyManagement.setIsAddingDep(false)}
                  childEntityType={childEntityType}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EpicDependencyTab;
